/**
 * `useLakeIndex()` — React binding for the data lake's catalogue.
 *
 * `shared/useOemData.ts` owns the twelve *domain* files and gives each a typed
 * hook. The lake is not a domain: it is 271 individual documents plus one
 * payload-free index, fetched through the imperative `loadLakeIndex()` /
 * `loadLakeDoc()` pair so that opening one document does not download 2.0 MB.
 * Those two are promise-returning functions with no React binding, and both
 * the connector grid and the data browser need one.
 *
 * So this hook is the missing binding, and nothing more: the same
 * `{ data, loading, error }` contract `useOemData` hands back, over the same
 * module-level promise cache. It deliberately lives in this module rather than
 * in `shared/` — the lake is the Integration section's subject matter, and
 * `shared/useOemData.ts` is a shared file this lane does not own.
 *
 * UI layer support (a hook, so React is allowed); the cache and the fetch both
 * live in `shared/useOemData.ts`.
 *
 * @see src/versions/v1/shared/useOemData.ts — `loadLakeIndex` / `loadLakeDoc`
 * @see src/versions/v1/shared/lake-drawer.ts — the drawer opener over the same pair
 */

import { useEffect, useRef, useState } from 'react';
import type { LakeDocument, LakeIndex } from '../../schema/document';
import { loadLakeDoc, loadLakeIndex } from '../../shared/useOemData';

/** What {@link useLakeIndex} returns. */
export interface LakeIndexState {
  /** The 271-row catalogue, or `null` until it has loaded (or if it failed). */
  index: LakeIndex | null;
  loading: boolean;
  error: Error | null;
}

/** Load `lake/index.json`, sharing the request with every other caller. */
export function useLakeIndex(): LakeIndexState {
  const [state, setState] = useState<LakeIndexState>({
    index: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let live = true;
    loadLakeIndex().then(
      (index) => {
        if (live) setState({ index, loading: false, error: null });
      },
      (cause: unknown) => {
        if (!live) return;
        setState({
          index: null,
          loading: false,
          error: cause instanceof Error ? cause : new Error(String(cause)),
        });
      },
    );
    return () => {
      live = false;
    };
  }, []);

  return state;
}

/** What {@link useLakeDocument} returns. */
export interface LakeDocumentState {
  doc: LakeDocument | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Load one lake document by slug. `null` loads nothing and reports idle.
 *
 * Guards on the slug the request was started for, so switching documents
 * mid-flight cannot let the first response overwrite the second.
 */
export function useLakeDocument(slug: string | null): LakeDocumentState {
  const [state, setState] = useState<LakeDocumentState>({
    doc: null,
    loading: false,
    error: null,
  });
  const wanted = useRef<string | null>(slug);

  useEffect(() => {
    wanted.current = slug;
    if (!slug) {
      setState({ doc: null, loading: false, error: null });
      return;
    }
    setState({ doc: null, loading: true, error: null });
    loadLakeDoc(slug).then(
      (doc) => {
        if (wanted.current === slug) setState({ doc, loading: false, error: null });
      },
      (cause: unknown) => {
        if (wanted.current !== slug) return;
        setState({
          doc: null,
          loading: false,
          error: cause instanceof Error ? cause : new Error(String(cause)),
        });
      },
    );
  }, [slug]);

  return state;
}
